import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { showAPI, classEntryAPI, payoutAPI, sidepotAPI, rolloverAPI, reservationAPI, resultsAPI, scoreAPI } from '../utils/api';
import { ArrowsClockwise, Eye, X, Warning, ShareNetwork, Check, QrCode } from '@phosphor-icons/react';
import { ShowQRCodeModal } from '../components/show/ShowQRCodeModal';
import { getDivisionColors } from '../utils/divisionColors';
import { toast } from 'sonner';
import axios from 'axios';

import { ShowHeader } from '../components/show/ShowHeader';
import { ContactProducerButton } from '../components/show/ContactProducerButton';
import { ClassList } from '../components/show/ClassList';
import { EntryTable } from '../components/show/EntryTable';
import { SidepotPanel } from '../components/show/SidepotPanel';
import { SidepotEditModal } from '../components/show/SidepotEditModal';
import { RolloverPanel } from '../components/show/RolloverPanel';
import { PayoutPanel } from '../components/show/PayoutPanel';
import { DivisionModal } from '../components/show/DivisionModal';
import { AveragesPanel } from '../components/show/AveragesPanel';
import { TargetPanel } from '../components/show/TargetPanel';
import FinalsQualifiersPanel from '../components/show/FinalsQualifiersPanel';
import { ReservationsTable } from '../components/show/ReservationsTable';
import { StaffReservations } from '../components/show/StaffReservations';
import { ActivityFeed } from '../components/show/ActivityFeed';
import SponsorShowModal from '../components/show/SponsorShowModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ShowDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [show, setShow] = useState(null);
  const [loading, setLoading] = useState(true);

  const [configuringClass, setConfiguringClass] = useState(null);
  const [divisionType, setDivisionType] = useState('NONE');
  const [divisionSplit, setDivisionSplit] = useState(0.5);

  const [selectedClass, setSelectedClass] = useState(null);
  const [showSponsorModal, setShowSponsorModal] = useState(false);
  const [classEntries, setClassEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [riderName, setRiderName] = useState('');
  const [horseName, setHorseName] = useState('');
  const [drawGenerated, setDrawGenerated] = useState(false);

  const [showPayouts, setShowPayouts] = useState(false);
  const [placesPaid, setPlacesPaid] = useState(1);
  const [divisionPlaces, setDivisionPlaces] = useState({'1D': 1, '2D': 1, '3D': 1, '4D': 1});
  // v242 — Producer-editable seconds-behind-fastest per division. Element 0
  // is always 0 (leader). Defaults follow standard barrel-racing splits.
  const [divisionSplits, setDivisionSplits] = useState([0, 0.5, 1.0, 2.0]);
  const [divisionPoolPct, setDivisionPoolPct] = useState({'1D': 50, '2D': 50});
  const [payoutData, setPayoutData] = useState(null);
  const [payoutLoading, setPayoutLoading] = useState(false);
  // v260 — Manual override for total pool ($). '' = use auto-computed pool.
  // Producers turn this on for finals classes where the money comes from
  // Go 1 + Go 2, not the finals roster.
  const [poolOverride, setPoolOverride] = useState('');
  const [publishLoading, setPublishLoading] = useState(false);

  const [panelTab, setPanelTab] = useState('entries');

  const [selectedSidepot, setSelectedSidepot] = useState(null);
  const [sidepotResults, setSidepotResults] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [sidepotLoading, setSidepotLoading] = useState(false);
  // Inline "calculate payouts" controls on the expanded sidepot card. Each
  // producer-visible card carries its own places-paid + busy state so one
  // calculation doesn't lock every card.
  const [sidepotPlacesPaid, setSidepotPlacesPaid] = useState(1);
  // Per-division pool % + places paid inputs for 2D/3D/4D sidepots. Keyed by
  // sidepot id so switching between cards doesn't clobber the other's entries.
  const [sidepotDivPct, setSidepotDivPct] = useState({});
  const [sidepotDivPlaces, setSidepotDivPlaces] = useState({});
  const [sidepotCalcing, setSidepotCalcing] = useState(false);
  const [sidepotModalOpen, setSidepotModalOpen] = useState(false);
  const [sidepotBeingEdited, setSidepotBeingEdited] = useState(null);
  // Confirmation dialog for removing a single rider from a sidepot. Set
  // by clicking the ✗ next to an entrant; null when closed.
  // Shape: { entry_id, rider_name, horse_name, is_rollover, sidepot_id,
  //          sidepot_name, sidepot_fee, removing? }
  const [removeFromSidepotConfirm, setRemoveFromSidepotConfirm] = useState(null);

  const [rolloverCandidates, setRolloverCandidates] = useState([]);
  const [rolloverSourceClasses, setRolloverSourceClasses] = useState([]);
  const [rolloverSourceName, setRolloverSourceName] = useState('');
  const [rolloverSourceId, setRolloverSourceId] = useState(null);
  const [rolloverLoading, setRolloverLoading] = useState(false);
  const [showRolloverPicker, setShowRolloverPicker] = useState(false);
  const [selectedRolloverIds, setSelectedRolloverIds] = useState([]);

  const csvInputRef = useRef(null);

  const userRoles = user?.roles || [user?.role];
  const isMaster = user && userRoles.some(r => ['MASTER', 'LMASTER'].includes(r));
  // v266 — Frontend must mirror the backend `user_can_edit_show` rule:
  //   1. Platform admins (MASTER/LMASTER) always pass.
  //   2. The show's CREATOR always retains admin (regardless of org state).
  //   3. ORG_ADMIN of the show's org, OR a designated editor of that org.
  // Previously producers who ran solo shows (no org) or transferred orgs
  // lost their admin controls and the UI treated them as riders.
  const isCreator = !!(user && show && show.created_by && show.created_by === user.id);
  const isDesignatedEditor = user && show?.organization_id
    && (user.editor_org_ids || []).includes(show.organization_id);
  const isShowOwner = user && (
    isMaster ||
    isCreator ||
    (userRoles.includes('ORG_ADMIN') && user.organization_id && show?.organization_id === user.organization_id) ||
    isDesignatedEditor
  );
  const isAdmin = isShowOwner;

  // v237 — Share button state. Producer taps Share → we copy the CANONICAL
  // public URL (`https://<host>/shows/{id}`) to clipboard and flash a
  // check icon. Guaranteed to open the show page for anyone (this route
  // is not gated by ProtectedRoute), so contestants see everything without
  // a forced login.
  const [shareCopied, setShareCopied] = useState(false);
  // v238 — QR modal state. Producer taps "QR Code" → we render a scannable
  // QR of the canonical public URL with download / print / copy actions.
  const [showQrModal, setShowQrModal] = useState(false);
  const publicShowUrl = typeof window !== 'undefined' ? `${window.location.origin}/shows/${id}` : `/shows/${id}`;
  const handleShare = async () => {
    const url = publicShowUrl;
    try {
      if (navigator.share) {
        await navigator.share({ title: show?.name || 'Horse Show', url });
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2500);
        return;
      }
      await navigator.clipboard.writeText(url);
      toast.success('Public show link copied to clipboard');
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2500);
    } catch {
      // Clipboard blocked (older browsers / non-https) — fall back to a
      // prompt window so the producer can copy manually.
      window.prompt('Copy this public link and share it:', url);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadShow(); }, [id, user]);

  // Auto-refresh the currently-expanded sidepot's standings so producers
  // see new times appear live without having to click out and back in.
  // Polls every 8s while a sidepot card is open; cleared on unmount or
  // when the producer switches to a different sidepot. We refetch ONLY
  // the sidepot results endpoint — not the whole show — to keep the
  // payload tiny and avoid clobbering local UI state (e.g. the pool %
  // inputs the producer might be editing).
  useEffect(() => {
    if (!selectedSidepot?.id) return undefined;
    const tick = async () => {
      try {
        const { data } = await sidepotAPI.results(id, selectedSidepot.id);
        // Only update if the sidepot is still the same one — guard
        // against late responses landing after a user-driven switch.
        setSidepotResults(prev => {
          if (!selectedSidepot?.id) return prev;
          return data;
        });
      } catch { /* swallow — transient network blips are non-fatal */ }
    };
    const handle = setInterval(tick, 8000);
    return () => clearInterval(handle);
  }, [selectedSidepot?.id, id]);

  const loadShow = async () => {
    try {
      const { data } = await showAPI.get(id);
      setShow(data);
      if (user && ['MASTER', 'ORG_ADMIN', 'LMASTER'].some(r => (user.roles || [user.role]).includes(r))) {
        try {
          const { data: resData } = await reservationAPI.getForShow(id);
          setReservations(resData.reservations || []);
        } catch (err) { console.warn('Failed to load reservations:', err.message); }
      }
    } catch { toast.error('Failed to load show details'); }
    finally { setLoading(false); }
  };

  const handleStateChange = async (newState) => {
    try { await showAPI.updateState(id, newState); toast.success(`Show state changed to ${newState}`); loadShow(); }
    catch { toast.error('Failed to update show state'); }
  };

  const handleDeleteShow = async () => {
    try {
      await showAPI.delete(id);
      toast.success('Show deleted successfully');
      navigate('/dashboard');
    } catch {
      toast.error('Failed to delete show');
    }
  };

  const handleConfigureDivisions = (cls) => {
    setConfiguringClass(cls);
    setDivisionType(cls.division_type || 'NONE');
    setDivisionSplit(cls.division_split || 0.5);
  };

  const handleSaveDivisionConfig = async () => {
    try {
      await axios.patch(`${BACKEND_URL}/api/shows/${id}/classes/${configuringClass.id}`, {}, {
        params: { division_type: divisionType, division_split: divisionType === '2D' ? divisionSplit : null }, withCredentials: true
      });
      toast.success('Division configuration saved'); setConfiguringClass(null); loadShow();
    } catch { toast.error('Failed to save configuration'); }
  };

  const loadEntries = useCallback(async (cls) => {
    setEntriesLoading(true);
    try { const { data } = await classEntryAPI.list(id, cls.id); setClassEntries(data); setDrawGenerated(data.some(e => e.draw_number !== null)); }
    catch { setClassEntries([]); }
    finally { setEntriesLoading(false); }
  }, [id]);

  // Auto-refresh class entries (names, times, payment status) every 8s
  // while a class panel is open. Lets the producer/office see new
  // entries land, payments flip to PAID, and scores show up live as the
  // scorekeeper enters them — without having to click out and back in.
  //
  // Stable across renders by tracking only the class ID; payload merges
  // are silent (no spinners) so the producer's focus inside EntryTable
  // (e.g. editing draw numbers) isn't disrupted.
  useEffect(() => {
    if (!selectedClass?.id) return undefined;
    const cls = selectedClass;
    const tick = async () => {
      try {
        const { data } = await classEntryAPI.list(id, cls.id);
        // Late-response guard — bail if the producer switched classes.
        if (selectedClass?.id !== cls.id) return;
        setClassEntries(data);
        setDrawGenerated(data.some(e => e.draw_number !== null));
      } catch { /* transient — keep prior entries visible */ }
    };
    const handle = setInterval(tick, 8000);
    return () => clearInterval(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClass?.id, id]);

  const handleClosePanel = () => { setSelectedClass(null); setClassEntries([]); setShowAddEntry(false); setEditingEntry(null); };

  const handleAddEntry = async (extraClassIds = []) => {
    if (!riderName.trim()) { toast.error('Rider name is required'); return; }
    // Always include the currently-selected class, plus any additional
    // classes the producer ticked on the add form.
    const targetIds = Array.from(new Set([selectedClass.id, ...(extraClassIds || [])]));
    try {
      const results = await Promise.allSettled(
        targetIds.map((cid) =>
          classEntryAPI.add(id, cid, { rider_name: riderName.trim(), horse_name: horseName.trim() })
        )
      );
      const added = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.length - added;
      if (added > 0) toast.success(
        added === 1 ? 'Entry added' : `Added to ${added} classes`
      );
      if (failed > 0) {
        const firstErr = results.find((r) => r.status === 'rejected');
        toast.error(firstErr?.reason?.response?.data?.detail || `${failed} add(s) failed`);
      }
      setRiderName(''); setHorseName(''); setShowAddEntry(false);
      loadEntries(selectedClass);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add entry');
    }
  };

  const handleUpdateEntry = async () => {
    if (!riderName.trim()) { toast.error('Rider name is required'); return; }
    try {
      await classEntryAPI.update(id, selectedClass.id, editingEntry.id, { rider_name: riderName.trim(), horse_name: horseName.trim() });
      toast.success('Entry updated'); setRiderName(''); setHorseName(''); setEditingEntry(null); loadEntries(selectedClass);
    } catch { toast.error('Failed to update entry'); }
  };

  const handleDeleteEntry = async (entryId, entry = null) => {
    // If this entry has rollovers attached, warn the producer that the
    // rollover children will be cascade-deleted (matches the backend
    // cascade introduced in v206 — `rolled_to` is the same array used
    // by the in-list badge so the dialog can name each destination).
    const targets = Array.isArray(entry?.rolled_to) ? entry.rolled_to : [];
    let msg = 'Remove this entry?';
    if (targets.length > 0) {
      const names = targets
        .map((t) => t.class_name || t.sidepot_name || 'Unknown')
        .join(', ');
      msg =
        `This rider has rollover${targets.length > 1 ? 's' : ''} into ` +
        `${names}.\n\nDeleting will ALSO remove them from ` +
        `${targets.length > 1 ? 'those classes' : 'that class'}. ` +
        `Continue?`;
    }
    if (!window.confirm(msg)) return;
    try {
      await classEntryAPI.remove(id, selectedClass.id, entryId);
      toast.success(
        targets.length > 0
          ? `Entry removed (also cleared from ${targets.length} other class${targets.length > 1 ? 'es' : ''})`
          : 'Entry removed'
      );
      loadEntries(selectedClass);
    } catch { toast.error('Failed to remove entry'); }
  };

  const handleClearAllEntries = async () => {
    try {
      const { data } = await classEntryAPI.clearAll(id, selectedClass.id);
      toast.success(`Cleared ${data.deleted} entries from ${selectedClass.name}`);
      loadEntries(selectedClass);
    } catch { toast.error('Failed to clear entries'); }
  };

  const handleQuickAddEntries = async (riders) => {
    let added = 0;
    let dupes = 0;
    for (const rider of riders) {
      try {
        await classEntryAPI.add(id, selectedClass.id, { rider_name: rider.rider_name, horse_name: rider.horse_name || '' });
        added++;
      } catch (err) {
        if (err.response?.status === 409) dupes++;
        else dupes++;
      }
    }
    let msg = `${added} entr${added === 1 ? 'y' : 'ies'} added to ${selectedClass.name}`;
    if (dupes > 0) msg += ` (${dupes} skipped)`;
    toast.success(msg);
    loadEntries(selectedClass);
  };

  const handleGenerateDraw = async () => {
    if (classEntries.length === 0) { toast.error('Add entries first'); return; }
    try { const { data } = await classEntryAPI.generateDraw(id, selectedClass.id); toast.success(`Draw generated for ${data.total_entries} entries`); loadEntries(selectedClass); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to generate draw'); }
  };

  const handleReverseDraw = async () => {
    if (!selectedClass) return;
    try {
      const { data } = await classEntryAPI.reverseDraw(id, selectedClass.id);
      toast.success(data.message || 'Draw reversed');
      loadEntries(selectedClass);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reverse draw');
    }
  };

  const handleCopyDraw = async (sourceClassId, reverse) => {
    if (!selectedClass || !sourceClassId) return;
    try {
      const { data } = await classEntryAPI.copyDraw(id, selectedClass.id, sourceClassId, reverse);
      toast.success(data.message || 'Draw copied');
      if (data.unmatched_count > 0) {
        toast.info(
          `${data.unmatched_count} rider${data.unmatched_count === 1 ? '' : 's'} had no match in source class — leave empty or re-generate.`
        );
      }
      loadEntries(selectedClass);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to copy draw');
    }
  };

  const startEditing = (entry) => { setEditingEntry(entry); setRiderName(entry.rider_name); setHorseName(entry.horse_name || ''); setShowAddEntry(false); };
  const cancelEdit = () => { setEditingEntry(null); setRiderName(''); setHorseName(''); };

  const handleCSVImport = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    const isAI = ['pdf', 'jpg', 'jpeg', 'png', 'docx', 'doc'].includes(ext);
    if (isAI) toast.info('AI is reading your document... This may take a moment');
    try {
      const { data } = await classEntryAPI.bulkImport(id, selectedClass.id, file);
      let msg = `${data.added} entries imported`;
      if (data.scores_added > 0) msg += ` with ${data.scores_added} times`;
      if (data.duplicates_skipped > 0) msg += ` (${data.duplicates_skipped} duplicate${data.duplicates_skipped > 1 ? 's' : ''} skipped)`;
      toast.success(msg);
      if (data.duplicates_skipped > 0) toast.info(`${data.duplicates_skipped} duplicate rider/horse combo${data.duplicates_skipped > 1 ? 's' : ''} already existed and were skipped`);
      if (data.errors?.length > 0) toast.error(`${data.errors.length} rows skipped`);
      loadEntries(selectedClass);
    } catch (error) { toast.error(error.response?.data?.detail || 'Import failed'); }
    e.target.value = '';
  };

  const handleCalculatePayouts = async () => {
    setPayoutLoading(true);
    try {
      const hasDivisions = selectedClass.division_type && !['NONE', 'OVERALL'].includes(selectedClass.division_type);
      const body = { overall_places: placesPaid, division_places: hasDivisions ? { ALL: placesPaid } : {}, division_pool_pct: hasDivisions ? divisionPoolPct : {} };
      // v260 — Include the manual pool override when the producer typed one.
      if (poolOverride !== '' && poolOverride !== null && !Number.isNaN(Number(poolOverride))) {
        body.total_pool_override = Number(poolOverride);
      }
      const { data } = await payoutAPI.calculate(selectedClass.id, body);
      setPayoutData(data);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to calculate payouts'); }
    finally { setPayoutLoading(false); }
  };

  const handlePublishResults = async () => {
    setPublishLoading(true);
    try {
      await resultsAPI.publish(selectedClass.id);
      toast.success('Results & payouts published!');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to publish results'); }
    finally { setPublishLoading(false); }
  };

  const handleSetClassStatus = async (classId, status) => {
    try {
      await classEntryAPI.setClassStatus(id, classId, status);
      toast.success(`Class set to ${status}`);
      loadShow();
    } catch { toast.error('Failed to update class status'); }
  };

  const handleRolloverEntry = async (entry, targetClassId, targetSidepotId = null) => {
    try {
      const { data } = await classEntryAPI.rolloverEntry(id, selectedClass.id, entry.id, targetClassId, targetSidepotId);
      if (targetSidepotId) {
        toast.success(data.message || `${entry.rider_name} added to sidepot`);
      } else {
        const targetName = show.classes.find(c => c.id === targetClassId)?.name || 'target class';
        toast.success(`${entry.rider_name} rolled over to ${targetName}${data.score_copied ? ' (with time)' : ''}`);
      }
      loadEntries(selectedClass);
    } catch (error) { toast.error(error.response?.data?.detail || 'Rollover failed'); }
  };

  const handleSubmitTime = async (entry, time, status) => {
    try {
      await scoreAPI.submit({
        entry_id: entry.id,
        class_id: selectedClass.id,
        time: time,
        knocks: 0,
        status: status
      });
      toast.success(`${status === 'SCRATCH' ? 'NT' : time?.toFixed(3)} recorded for ${entry.rider_name}`);
      loadEntries(selectedClass);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to submit time'); }
  };

  const handleTogglePayment = async (entry) => {
    const cycle = ['UNPAID', 'PAID', 'COMP'];
    const current = entry.payment_status || 'UNPAID';
    const next = cycle[(cycle.indexOf(current) + 1) % cycle.length];
    try { await axios.patch(`${BACKEND_URL}/api/shows/${id}/classes/${selectedClass.id}/entries/${entry.id}/payment?status=${next}`, {}, { withCredentials: true }); loadEntries(selectedClass); }
    catch { toast.error('Failed to update payment status'); }
  };

  const handleToggleSidepot = async (entry, sidepotId) => {
    try { await classEntryAPI.toggleSidepot(id, selectedClass.id, entry.id, sidepotId); loadEntries(selectedClass); }
    catch { toast.error('Failed to update sidepot'); }
  };

  const loadSidepotResults = async (sidepot) => {
    setSelectedSidepot(sidepot); setSidepotLoading(true);
    // Seed per-division pool % + places paid from the sidepot doc (or the
    // site-standard split fallback) so the admin config panel opens
    // populated instead of blank.
    //
    // Site-standard division splits (matches class payouts):
    //   2D → 60/40
    //   3D → 50/30/20
    //   4D → 40/30/20/10
    // These are the defaults used unless the producer explicitly overrides
    // them on a saved sidepot's `division_pool_pct` field.
    const STANDARD_SPLIT = {
      '2D': [60, 40],
      '3D': [50, 30, 20],
      '4D': [40, 30, 20, 10],
    };
    const divNames = sidepot.division_type === '4D' ? ['1D','2D','3D','4D']
      : sidepot.division_type === '3D' ? ['1D','2D','3D']
      : sidepot.division_type === '2D' ? ['1D','2D'] : [];
    if (divNames.length > 0) {
      setSidepotDivPct(prev => {
        if (prev[sidepot.id]) return prev;
        const standard = STANDARD_SPLIT[sidepot.division_type] || [];
        const seeded = {};
        divNames.forEach((n, i) => {
          // Saved overrides win; otherwise apply the site-standard split.
          seeded[n] = sidepot.division_pool_pct?.[n] ?? standard[i] ?? 0;
        });
        return { ...prev, [sidepot.id]: seeded };
      });
      setSidepotDivPlaces(prev => {
        if (prev[sidepot.id]) return prev;
        const seeded = {};
        divNames.forEach(n => { seeded[n] = sidepot.division_places_paid?.[n] ?? 1; });
        return { ...prev, [sidepot.id]: seeded };
      });
    }
    try { const { data } = await sidepotAPI.results(id, sidepot.id); setSidepotResults(data); }
    catch { setSidepotResults(null); }
    finally { setSidepotLoading(false); }
  };

  const handleCalculateSidepotPayouts = async (sidepotId, placesPaid, divisionPoolPct) => {
    try {
      const { data } = await sidepotAPI.calculatePayouts(sidepotId, {
        show_id: id,
        places_paid: placesPaid,
        division_pool_pct: divisionPoolPct || {},
      });
      setSidepotResults(data);
      toast.success(`Sidepot payouts calculated — ${data.places_paid} places paid, $${data.total_pool.toFixed(0)} pool`);
    } catch { toast.error('Failed to calculate sidepot payouts'); }
  };

  // Producer/admin pulls a single rider out of a sidepot — used for
  // misclicks during rider self-enrollment, accidental rollovers, or a
  // rider asking off. Refreshes the entrants list in place; saved
  // payouts are auto-invalidated server-side so a recalc will reflect
  // the new pool size.
  const handleRemoveFromSidepot = async ({ refund }) => {
    if (!removeFromSidepotConfirm) return;
    const { entry_id, sidepot_id, sidepot_name, rider_name } = removeFromSidepotConfirm;
    setRemoveFromSidepotConfirm(prev => ({ ...prev, removing: true }));
    try {
      await sidepotAPI.removeEntry(id, sidepot_id, entry_id, refund);
      toast.success(
        refund
          ? `${rider_name} removed from ${sidepot_name} — fee marked for refund`
          : `${rider_name} removed from ${sidepot_name}`
      );
      setRemoveFromSidepotConfirm(null);
      // Refresh entrants/results for the currently expanded sidepot.
      if (selectedSidepot?.id === sidepot_id) {
        try {
          const { data } = await sidepotAPI.results(id, sidepot_id);
          setSidepotResults(data);
        } catch { /* swallow — stale list is non-fatal */ }
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to remove rider from sidepot';
      toast.error(detail);
      setRemoveFromSidepotConfirm(prev => prev ? { ...prev, removing: false } : null);
    }
  };

  // Inline "Calculate" from the expanded sidepot card on the overview grid.
  // Mirrors SidepotPanel's flow but refreshes the live-standings payload
  // (`sidepotResults`) via `loadSidepotResults` so the card updates in place.
  const handleInlineCalcSidepot = async (sidepot) => {
    if (!sidepot?.id) return;
    const divNames = sidepot.division_type === '4D' ? ['1D','2D','3D','4D'] :
      sidepot.division_type === '3D' ? ['1D','2D','3D'] :
      sidepot.division_type === '2D' ? ['1D','2D'] : [];
    const hasDivs = divNames.length > 0;
    let divPct = {};
    let divPlaces = {};
    let overallPlaces = sidepotPlacesPaid;
    if (hasDivs) {
      const pctState = sidepotDivPct[sidepot.id] || {};
      const placesState = sidepotDivPlaces[sidepot.id] || {};
      const totalPct = divNames.reduce((s, n) => s + (Number(pctState[n]) || 0), 0);
      if (Math.abs(totalPct - 100) > 1) {
        toast.error(`Division pool % must sum to 100 (currently ${totalPct}%)`);
        return;
      }
      divNames.forEach(n => {
        divPct[n] = Number(pctState[n]) || 0;
        divPlaces[n] = Math.max(0, parseInt(placesState[n] ?? 1) || 0);
      });
    }
    setSidepotCalcing(true);
    try {
      const { data } = await sidepotAPI.calculatePayouts(sidepot.id, {
        show_id: id,
        places_paid: hasDivs ? Math.max(...Object.values(divPlaces), 1) : overallPlaces,
        division_pool_pct: divPct,
        division_places_paid: divPlaces,
      });
      toast.success(`Saved — $${(data.total_pool || 0).toFixed(0)} pool`);
      await loadSidepotResults(sidepot);
      loadShow(); // pull the now-persisted division_pool_pct + places back onto the sidepot
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to calculate payouts');
    } finally {
      setSidepotCalcing(false);
    }
  };

  const loadRolloverCandidates = async (cls) => {
    setRolloverLoading(true);
    try { const { data } = await rolloverAPI.getCandidates(id, cls.id); setRolloverCandidates(data.candidates || []); setRolloverSourceClasses(data.source_classes || []); }
    catch { setRolloverCandidates([]); setRolloverSourceClasses([]); }
    finally { setRolloverLoading(false); }
  };

  const handleSetRolloverFrom = async (classId, rolloverFromClassId, rolloverCategory) => {
    try { await rolloverAPI.setRolloverFrom(id, classId, rolloverFromClassId || null, rolloverCategory || null); toast.success(rolloverFromClassId ? 'Rollover source set' : 'Rollover source cleared'); loadShow(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to update rollover'); }
  };

  const handleAddRollovers = async () => {
    if (selectedRolloverIds.length === 0) { toast.error('Select at least one rider to roll over'); return; }
    try { const { data } = await rolloverAPI.createEntries(id, selectedClass.id, selectedRolloverIds); toast.success(`${data.added} rollover entries added`); setShowRolloverPicker(false); setSelectedRolloverIds([]); loadEntries(selectedClass); loadRolloverCandidates(selectedClass); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to add rollovers'); }
  };

  const handleRemoveRollover = async (entryId) => {
    if (!window.confirm('Remove this rollover entry?')) return;
    try { await rolloverAPI.removeEntry(id, selectedClass.id, entryId); toast.success('Rollover removed'); loadEntries(selectedClass); loadRolloverCandidates(selectedClass); }
    catch { toast.error('Failed to remove rollover'); }
  };

  const toggleRolloverSelection = (entryId) => {
    setSelectedRolloverIds(prev => prev.includes(entryId) ? prev.filter(x => x !== entryId) : [...prev, entryId]);
  };

  const handleSelectClass = (cls) => {
    setSelectedClass(cls); loadEntries(cls);
    setShowAddEntry(false); setEditingEntry(null); setPanelTab('entries');
    setPayoutData(null); setShowRolloverPicker(false); setSelectedRolloverIds([]);
    if (cls.rollover_from_class_ids?.length > 0 || cls.rollover_from_class_id) { loadRolloverCandidates(cls); }
    else { setRolloverCandidates([]); setRolloverSourceClasses([]); }
    // v242 — Hydrate division splits from the class doc if the producer
    // has previously saved them (so "set once, keep forever"). Otherwise
    // fall back to standard barrel-racing defaults for the division type.
    const savedPct = cls.division_pool_pct || {};
    const savedPlaces = cls.division_places || {};
    const hasSavedPct = Object.keys(savedPct).length > 0;
    const hasSavedPlaces = Object.keys(savedPlaces).length > 0;
    if (cls.division_type === '4D') {
      const defaultSplits = [0, 0.5, 1.0, 2.0];
      setDivisionPoolPct(hasSavedPct ? {'1D': savedPct['1D'] ?? 40, '2D': savedPct['2D'] ?? 30, '3D': savedPct['3D'] ?? 20, '4D': savedPct['4D'] ?? 10} : {'1D': 40, '2D': 30, '3D': 20, '4D': 10});
      setDivisionPlaces(hasSavedPlaces ? {'1D': savedPlaces['1D'] ?? 1, '2D': savedPlaces['2D'] ?? 1, '3D': savedPlaces['3D'] ?? 1, '4D': savedPlaces['4D'] ?? 1} : {'1D': 1, '2D': 1, '3D': 1, '4D': 1});
      setDivisionSplits(Array.isArray(cls.division_splits) && cls.division_splits.length === 4 ? cls.division_splits : defaultSplits);
    } else if (cls.division_type === '3D') {
      const defaultSplits = [0, 1.0, 2.0];
      setDivisionPoolPct(hasSavedPct ? {'1D': savedPct['1D'] ?? 50, '2D': savedPct['2D'] ?? 30, '3D': savedPct['3D'] ?? 20} : {'1D': 50, '2D': 30, '3D': 20});
      setDivisionPlaces(hasSavedPlaces ? {'1D': savedPlaces['1D'] ?? 1, '2D': savedPlaces['2D'] ?? 1, '3D': savedPlaces['3D'] ?? 1} : {'1D': 1, '2D': 1, '3D': 1});
      setDivisionSplits(Array.isArray(cls.division_splits) && cls.division_splits.length === 3 ? cls.division_splits : defaultSplits);
    } else if (cls.division_type === '2D') {
      setDivisionPoolPct(hasSavedPct ? {'1D': savedPct['1D'] ?? 60, '2D': savedPct['2D'] ?? 40} : {'1D': 60, '2D': 40});
      setDivisionPlaces(hasSavedPlaces ? {'1D': savedPlaces['1D'] ?? 1, '2D': savedPlaces['2D'] ?? 1} : {'1D': 1, '2D': 1});
      setDivisionSplits([0, Number(cls.division_split ?? 0.5)]);
    }
  };

  const handleUpdateClassPayback = async (classId, paybackPercent) => {
    try {
      await showAPI.updateClassPayback(id, classId, paybackPercent);
      toast.success(`Class payback updated to ${paybackPercent}%`);
      loadShow();
    } catch { toast.error('Failed to update class payback'); }
  };

  if (loading) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><div className="shimmer h-12 w-12 rounded-full"></div></div>;
  if (!show) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><div className="text-center"><h2 className="text-2xl font-bold text-white mb-4">Show not found</h2><button onClick={() => navigate(user ? '/dashboard' : '/')} className="px-6 py-3 bg-[#0096FF] text-white rounded-md" data-testid="back-dashboard-btn">{user ? 'Back to Dashboard' : 'Back to Home'}</button></div></div>;

  const isSingleDayShow = show.start_date === show.end_date;
  const classesByDay = {};
  show.classes.forEach(cls => {
    const day = (!isSingleDayShow && cls.scheduled_day) ? cls.scheduled_day : 'All Days';
    if (!classesByDay[day]) classesByDay[day] = [];
    classesByDay[day].push(cls);
  });

  const sortedEntries = [...classEntries].sort((a, b) => {
    if (a.draw_number && b.draw_number) return a.draw_number - b.draw_number;
    if (a.draw_number) return -1;
    if (b.draw_number) return 1;
    return 0;
  });

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-0">
        <ShowHeader show={show} isAdmin={isAdmin} onStateChange={handleStateChange} onDelete={handleDeleteShow} />

        {/* Showbill Image */}
        {/* Live Now Banner */}
        {show.classes.some(c => c.status === 'LIVE') && (
          <div className="mb-6 bg-[#FF3B30]/10 border border-[#FF3B30]/40 rounded-lg p-4" data-testid="live-now-banner">
            <div className="flex items-center gap-3 mb-3">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF3B30] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-[#FF3B30]"></span>
              </span>
              <h3 className="text-[#FF3B30] font-bold text-lg" style={{fontFamily: 'Outfit'}}>Live Now</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {show.classes.filter(c => c.status === 'LIVE').map(cls => (
                <button
                  key={cls.id}
                  onClick={() => navigate(`/live/${cls.id}`)}
                  className="flex items-center gap-2 px-4 py-2 bg-[#FF3B30] hover:bg-[#FF3B30]/80 text-white font-bold rounded-md transition-colors text-sm"
                  data-testid={`live-banner-btn-${cls.id}`}
                >
                  <Eye size={16} weight="bold" /> {cls.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {(show.state === 'PUBLISHED' || show.state === 'LIVE' || show.state === 'COMPLETED') && (
          <div className="mb-6 sm:mb-8 flex flex-wrap gap-3">
            {(show.state === 'PUBLISHED' || show.state === 'LIVE') && (
              <button onClick={() => navigate(`/shows/${id}/enter`)} className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-3.5 bg-[#0096FF] hover:bg-[#00D4FF] text-white font-bold rounded-lg transition-colors text-sm sm:text-base" data-testid="enter-show-btn">Enter this Show</button>
            )}
            <button onClick={() => navigate(`/shows/${id}/results`)} className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-3.5 bg-[#34C759]/10 border border-[#34C759]/30 hover:bg-[#34C759]/20 text-[#34C759] font-bold rounded-lg transition-colors text-sm sm:text-base" data-testid="view-results-btn">View Results</button>
            <button
              onClick={handleShare}
              data-testid="share-show-btn"
              className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-3.5 bg-[#FFCC00]/10 border border-[#FFCC00]/40 hover:bg-[#FFCC00]/20 text-[#FFCC00] font-bold rounded-lg transition-colors text-sm sm:text-base flex items-center gap-2 justify-center"
              title="Copy a public link to this show — anyone can view it without logging in"
            >
              {shareCopied ? <><Check size={16} weight="bold" /> Link copied!</> : <><ShareNetwork size={16} weight="bold" /> Share Show</>}
            </button>
            {isAdmin && (
              <button
                onClick={() => setShowQrModal(true)}
                data-testid="generate-qr-code-btn"
                className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-3.5 bg-[#0A0A0A] border border-white/10 hover:border-[#FFCC00]/60 text-white font-bold rounded-lg transition-colors text-sm sm:text-base flex items-center gap-2 justify-center"
                title={show.qr_generated_at
                  ? 'View the previously generated QR code (there is only one QR per show)'
                  : 'Generate a scannable QR code for social media, flyers, or on-site posting'}
              >
                <QrCode size={16} weight="bold" />
                {show.qr_generated_at ? 'View QR Code' : 'Generate QR Code'}
              </button>
            )}
            <button
              onClick={() => setShowSponsorModal(true)}
              className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-3.5 bg-[#FF3B30]/10 border border-[#FF3B30]/40 hover:bg-[#FF3B30]/20 text-[#FF3B30] font-bold rounded-lg transition-colors text-sm sm:text-base flex items-center gap-2 justify-center"
              data-testid="sponsor-show-btn"
            >
              <span className="text-base">&#9829;</span> Sponsor This Show
            </button>
            <ContactProducerButton show={show} />
          </div>
        )}

        {/* v237 — Anonymous viewer reassurance. If a rider opens the shared
            link and isn't logged in, tell them upfront that everything's
            visible and they only need to log in to submit an entry. Kills
            the "wait, do I need an account to look at this?" hesitation. */}
        {!user && (show.state === 'PUBLISHED' || show.state === 'LIVE' || show.state === 'COMPLETED') && (
          <div
            className="mb-6 sm:mb-8 bg-[#0096FF]/5 border border-[#0096FF]/30 rounded-lg px-4 py-3 flex flex-wrap items-center gap-3"
            data-testid="anonymous-viewer-banner"
          >
            <Eye size={18} weight="duotone" className="text-[#0096FF] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white font-semibold">You&rsquo;re viewing as a guest</p>
              <p className="text-xs text-[#9E9E9E] mt-0.5">
                Everything a contestant can see is on this page &mdash; classes, sidepots, draws, results.
                You only need to log in when you&rsquo;re ready to enter.
              </p>
            </div>
          </div>
        )}

        {/* Show-Wide Payback Control */}
        {isAdmin && show.classes.length > 0 && (
          <div className="mb-6 bg-[#161616] border border-[#262626] rounded-lg p-4" data-testid="show-payback-control">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-white">Show Payback:</span>
                <select
                  value={show.classes[0]?.payback_percent || 70}
                  onChange={async (e) => {
                    const val = parseFloat(e.target.value);
                    try {
                      await showAPI.updatePayback(id, val);
                      toast.success(`All classes set to ${val}% payback`);
                      loadShow();
                    } catch { toast.error('Failed to update payback'); }
                  }}
                  className="px-3 py-1.5 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#0096FF]"
                  data-testid="show-payback-select"
                >
                  {[50, 60, 65, 70, 75, 80, 85, 90, 100].map(p => (
                    <option key={p} value={p}>{p}%</option>
                  ))}
                </select>
              </div>
              <p className="text-[#525252] text-xs">Applies to all classes. Edit individual classes to override.</p>
            </div>
          </div>
        )}

        <div className={`grid gap-6 ${selectedClass ? 'grid-cols-1 lg:grid-cols-5' : 'grid-cols-1'}`}>
          <div className={selectedClass ? 'lg:col-span-2' : ''}>
            <ClassList classesByDay={classesByDay} selectedClass={selectedClass} isAdmin={isAdmin} onSelectClass={handleSelectClass} onConfigureDivisions={handleConfigureDivisions} onSetClassStatus={handleSetClassStatus} onUpdateClassPayback={handleUpdateClassPayback} />
          </div>

          {selectedClass && (
            <div className="lg:col-span-3" data-testid="entries-panel">
              <div className="bg-[#161616] border border-[#262626] rounded-lg overflow-hidden sticky top-24">
                {/* Tab Switcher */}
                <div className="px-5 py-2 border-b border-[#262626] flex gap-1 bg-[#0A0A0A]/30">
                  <button onClick={() => setPanelTab('entries')} data-testid="tab-entries" className={`px-3 py-1.5 rounded text-sm font-semibold transition-colors ${panelTab === 'entries' ? 'bg-[#0096FF]/15 text-[#0096FF]' : 'text-[#9E9E9E] hover:text-white'}`}>Entries & Draw</button>
                  {show.sidepots && show.sidepots.length > 0 && (
                    <button onClick={() => setPanelTab('sidepots')} data-testid="tab-sidepots" className={`px-3 py-1.5 rounded text-sm font-semibold transition-colors ${panelTab === 'sidepots' ? 'bg-[#FACC15]/15 text-[#FACC15]' : 'text-[#9E9E9E] hover:text-white'}`}>Sidepots</button>
                  )}
                  {(selectedClass?.rollover_from_class_ids?.length > 0 || selectedClass?.rollover_from_class_id) && (
                    <button onClick={() => { setPanelTab('rollovers'); loadRolloverCandidates(selectedClass); }} data-testid="tab-rollovers" className={`px-3 py-1.5 rounded text-sm font-semibold transition-colors flex items-center gap-1.5 ${panelTab === 'rollovers' ? 'bg-[#FF9500]/15 text-[#FF9500]' : 'text-[#9E9E9E] hover:text-white'}`}><ArrowsClockwise size={13} weight="bold" /> Rollovers</button>
                  )}
                  <button onClick={() => setPanelTab('payouts')} data-testid="tab-payouts" className={`px-3 py-1.5 rounded text-sm font-semibold transition-colors ${panelTab === 'payouts' ? 'bg-[#34C759]/15 text-[#34C759]' : 'text-[#9E9E9E] hover:text-white'}`}>Payouts</button>
                  {selectedClass?.is_finals && (
                    <button onClick={() => setPanelTab('qualifiers')} data-testid="tab-qualifiers" className={`px-3 py-1.5 rounded text-sm font-semibold transition-colors flex items-center gap-1.5 ${panelTab === 'qualifiers' ? 'bg-[#FACC15]/15 text-[#FACC15]' : 'text-[#9E9E9E] hover:text-white'}`}>
                      <span>&#9733;</span> Qualifiers
                    </button>
                  )}
                </div>

                {panelTab === 'entries' && (
                  <EntryTable
                    selectedClass={selectedClass} classEntries={classEntries} sortedEntries={sortedEntries}
                    entriesLoading={entriesLoading} drawGenerated={drawGenerated} showAddEntry={showAddEntry}
                    editingEntry={editingEntry} riderName={riderName} horseName={horseName} showState={show.state}
                    onSetShowAddEntry={setShowAddEntry} onSetRiderName={setRiderName} onSetHorseName={setHorseName}
                    onAddEntry={handleAddEntry} onUpdateEntry={handleUpdateEntry} onDeleteEntry={handleDeleteEntry}
                    onGenerateDraw={handleGenerateDraw} onStartEditing={startEditing} onCancelEdit={cancelEdit}
                    onReverseDraw={handleReverseDraw}
                    onCopyDraw={handleCopyDraw}
                    onTogglePayment={handleTogglePayment} onCSVImport={handleCSVImport} onClosePanel={handleClosePanel}
                    onGoToScoring={() => navigate(`/scoring/${selectedClass.id}`)}
                    onViewLive={() => navigate(`/live/${selectedClass.id}`)}
                    csvInputRef={csvInputRef}
                    onSubmitTime={handleSubmitTime}
                    onSaveEntry={handleUpdateEntry}
                    allClasses={show.classes}
                    allSidepots={show.sidepots || []}
                    onRolloverEntry={handleRolloverEntry}
                    onClearAllEntries={handleClearAllEntries}
                    isAdmin={isAdmin}
                    show={show}
                    showId={id}
                    onQuickAddEntries={handleQuickAddEntries}
                    onReloadEntries={() => loadEntries(selectedClass)}
                  />
                )}

                {panelTab === 'sidepots' && (
                  <SidepotPanel
                    show={show} selectedSidepot={selectedSidepot} sidepotResults={sidepotResults}
                    sidepotLoading={sidepotLoading} sortedEntries={sortedEntries}
                    onLoadSidepotResults={loadSidepotResults} onToggleSidepot={handleToggleSidepot}
                    onCalculateSidepotPayouts={handleCalculateSidepotPayouts}
                  />
                )}

                {panelTab === 'rollovers' && (selectedClass?.rollover_from_class_ids?.length > 0 || selectedClass?.rollover_from_class_id) && (
                  <RolloverPanel
                    classEntries={classEntries} rolloverCandidates={rolloverCandidates}
                    rolloverSourceClasses={rolloverSourceClasses} rolloverLoading={rolloverLoading}
                    showRolloverPicker={showRolloverPicker} selectedRolloverIds={selectedRolloverIds}
                    onSetShowRolloverPicker={setShowRolloverPicker} onSetSelectedRolloverIds={setSelectedRolloverIds}
                    onAddRollovers={handleAddRollovers} onRemoveRollover={handleRemoveRollover}
                    onToggleRolloverSelection={toggleRolloverSelection}
                  />
                )}

                {panelTab === 'payouts' && (
                  <PayoutPanel
                    show={show}
                    selectedClass={selectedClass} classEntries={classEntries} payoutData={payoutData}
                    payoutLoading={payoutLoading} placesPaid={placesPaid} divisionPlaces={divisionPlaces}
                    divisionPoolPct={divisionPoolPct}
                    poolOverride={poolOverride}
                    onSetPoolOverride={setPoolOverride}
                    onSetPlacesPaid={setPlacesPaid}
                    onSetDivisionPlaces={setDivisionPlaces} onSetDivisionPoolPct={setDivisionPoolPct}
                    onCalculatePayouts={handleCalculatePayouts}
                    onSavePayoutConfig={async () => {
                      // v244 — Save only pool% + places from this panel.
                      // Time splits belong to Show Edit → Class Settings.
                      try {
                        await showAPI.updateClassPayoutConfig(id, selectedClass.id, {
                          division_pool_pct: divisionPoolPct,
                          division_places: divisionPlaces,
                        });
                        toast.success('Payout config saved to this class');
                        loadShow();
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || 'Save failed');
                      }
                    }}
                  />
                )}
                {panelTab === 'qualifiers' && selectedClass?.is_finals && (
                  <FinalsQualifiersPanel
                    showId={id}
                    finalsClass={selectedClass}
                    canEdit={isAdmin}
                    onConfirmed={() => loadEntries(selectedClass)}
                  />
                )}
              </div>
            </div>
          )}
        </div>

        {isAdmin && (!show.sidepots || show.sidepots.length === 0) && (
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-white" style={{fontFamily: 'Outfit'}}>Sidepots</h2>
              <button
                type="button"
                onClick={() => { setSidepotBeingEdited(null); setSidepotModalOpen(true); }}
                className="px-3 py-1.5 bg-[#FACC15] hover:bg-[#FACC15]/80 text-black rounded-md text-xs font-bold"
                data-testid="add-sidepot-btn"
              >
                + Add Sidepot
              </button>
            </div>
            <div className="bg-[#161616] border border-dashed border-[#262626] rounded-lg p-8 text-center">
              <p className="text-[#9E9E9E] text-sm">No sidepots yet. Add one to let riders buy into extra payout pools.</p>
            </div>
          </div>
        )}

        {show.sidepots && show.sidepots.length > 0 && (
          <div className="mt-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white" style={{fontFamily: 'Outfit'}}>Sidepots</h2>
              {isAdmin && (
                <button
                  type="button"
                  onClick={() => { setSidepotBeingEdited(null); setSidepotModalOpen(true); }}
                  className="px-3 py-1.5 bg-[#FACC15] hover:bg-[#FACC15]/80 text-black rounded-md text-xs font-bold"
                  data-testid="add-sidepot-btn"
                >
                  + Add Sidepot
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {show.sidepots.map((sidepot) => {
                const isOpen = selectedSidepot?.id === sidepot.id;
                const isDiv = ['2D','3D','4D'].includes(sidepot.division_type);
                return (
                  <button
                    key={sidepot.id || sidepot.name}
                    type="button"
                    onClick={() => {
                      if (isOpen) {
                        setSelectedSidepot(null);
                        setSidepotResults(null);
                      } else {
                        loadSidepotResults(sidepot);
                      }
                    }}
                    data-testid={`sidepot-card-${sidepot.id || sidepot.name}`}
                    className={`text-left bg-[#161616] border rounded-lg p-5 transition-all ${
                      isOpen ? 'border-[#FACC15] ring-1 ring-[#FACC15]/40' : 'border-[#262626] hover:border-[#FACC15]/60'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3 gap-2">
                      <h4 className="text-lg font-bold text-white" style={{fontFamily: 'Outfit'}}>{sidepot.name}</h4>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {sidepot.division_type && sidepot.division_type !== 'NONE' && (
                          <span className="px-2 py-0.5 bg-[#0096FF]/20 text-[#0096FF] rounded text-[10px] font-bold">{sidepot.division_type}</span>
                        )}
                        {isAdmin && (
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => { e.stopPropagation(); setSidepotBeingEdited(sidepot); setSidepotModalOpen(true); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setSidepotBeingEdited(sidepot); setSidepotModalOpen(true); } }}
                            className="px-2 py-0.5 bg-[#262626] hover:bg-[#FACC15]/20 text-[#9E9E9E] hover:text-[#FACC15] rounded text-[10px] font-bold cursor-pointer transition-colors"
                            data-testid={`edit-sidepot-${sidepot.id}`}
                            title="Edit sidepot"
                          >
                            EDIT
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex justify-between text-sm mb-3">
                      <span className="text-[#9E9E9E]">Entry Fee:</span>
                      <span className="text-white font-semibold">${sidepot.entry_fee}</span>
                    </div>
                    {!isOpen && (
                      <p className="text-[11px] text-[#555] text-center pt-1 border-t border-[#262626]">
                        Tap to view standings
                      </p>
                    )}
                    {isOpen && (
                      <div className="border-t border-[#262626] pt-3 mt-2" data-testid={`sidepot-standings-${sidepot.id}`}>
                        {sidepotLoading && <p className="text-xs text-[#9E9E9E] text-center py-3">Loading…</p>}
                        {!sidepotLoading && sidepotResults && (
                          <>
                            <div className="flex items-center justify-between mb-2 text-[11px]">
                              <span className="text-[#9E9E9E]">
                                {sidepotResults.total_scored || 0} of {sidepotResults.total_entries || 0} scored
                              </span>
                              {sidepotResults.total_pool > 0 && (
                                <span className="text-[#34C759] font-semibold">${sidepotResults.total_pool} pool</span>
                              )}
                            </div>
                            {isAdmin && (() => {
                              const divNames = sidepot.division_type === '4D' ? ['1D','2D','3D','4D']
                                : sidepot.division_type === '3D' ? ['1D','2D','3D']
                                : sidepot.division_type === '2D' ? ['1D','2D'] : [];
                              const hasDivs = divNames.length > 0;
                              const spPct = sidepotDivPct[sidepot.id] || {};
                              const spPlaces = sidepotDivPlaces[sidepot.id] || {};
                              const pctTotal = divNames.reduce((s, n) => s + (Number(spPct[n]) || 0), 0);
                              const pctOk = !hasDivs || Math.abs(pctTotal - 100) < 1;
                              return (
                                <div
                                  className="mb-3 px-2.5 py-2.5 rounded bg-[#0B0B0B] border border-[#262626] space-y-2"
                                  onClick={(e) => e.stopPropagation()}
                                  data-testid={`sidepot-inline-calc-${sidepot.id}`}
                                >
                                  {hasDivs ? (
                                    <>
                                      <p className="text-[10px] font-semibold text-[#FACC15] uppercase tracking-wider">Per-division Payout Config</p>
                                      <div className="grid grid-cols-[auto_1fr_1fr] gap-x-2 gap-y-1 items-center text-[11px]">
                                        <span className="text-[#9E9E9E] text-[10px] uppercase tracking-wider">Div</span>
                                        <span className="text-[#9E9E9E] text-[10px] uppercase tracking-wider">Pool %</span>
                                        <span className="text-[#9E9E9E] text-[10px] uppercase tracking-wider">Places</span>
                                        {divNames.map((n) => (
                                          <React.Fragment key={n}>
                                            <span className="text-white font-bold">{n}</span>
                                            <div className="relative">
                                              <input
                                                type="number" min="0" max="100"
                                                value={spPct[n] ?? ''}
                                                onChange={(e) => setSidepotDivPct(prev => ({
                                                  ...prev,
                                                  [sidepot.id]: { ...(prev[sidepot.id] || {}), [n]: parseFloat(e.target.value) || 0 }
                                                }))}
                                                onClick={(e) => e.stopPropagation()}
                                                className="w-full px-2 py-1 bg-[#111] border border-[#262626] rounded text-white text-right pr-5"
                                                data-testid={`sidepot-div-pct-${sidepot.id}-${n}`}
                                              />
                                              <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[#555] text-[9px]">%</span>
                                            </div>
                                            <input
                                              type="number" min="0" max="15"
                                              value={spPlaces[n] ?? 1}
                                              onChange={(e) => setSidepotDivPlaces(prev => ({
                                                ...prev,
                                                [sidepot.id]: { ...(prev[sidepot.id] || {}), [n]: Math.max(0, parseInt(e.target.value) || 0) }
                                              }))}
                                              onClick={(e) => e.stopPropagation()}
                                              className="w-full px-2 py-1 bg-[#111] border border-[#262626] rounded text-white text-center"
                                              data-testid={`sidepot-div-places-${sidepot.id}-${n}`}
                                            />
                                          </React.Fragment>
                                        ))}
                                      </div>
                                      <div className="flex items-center justify-between">
                                        <span className={`text-[10px] ${pctOk ? 'text-[#34C759]' : 'text-[#FF3B30]'}`} data-testid={`sidepot-pct-total-${sidepot.id}`}>
                                          Total: {pctTotal}% {pctOk ? '' : '(must = 100%)'}
                                        </span>
                                        <button
                                          type="button"
                                          disabled={sidepotCalcing || !pctOk}
                                          onClick={(e) => { e.stopPropagation(); handleInlineCalcSidepot(sidepot); }}
                                          className="px-3 py-1 rounded bg-[#FACC15] text-black text-[11px] font-semibold hover:bg-[#FACC15]/80 disabled:opacity-50 disabled:cursor-not-allowed"
                                          data-testid={`sidepot-inline-calc-btn-${sidepot.id}`}
                                        >
                                          {sidepotCalcing ? 'Calculating…' : 'Calculate Payouts'}
                                        </button>
                                      </div>
                                    </>
                                  ) : (
                                    <div className="flex items-center gap-2">
                                      <label className="text-[10px] text-[#9E9E9E] uppercase tracking-wider">Places paid</label>
                                      <input
                                        type="number"
                                        min="1"
                                        max="15"
                                        value={sidepotPlacesPaid}
                                        onChange={(e) => setSidepotPlacesPaid(Math.max(1, parseInt(e.target.value) || 1))}
                                        onClick={(e) => e.stopPropagation()}
                                        className="w-14 bg-[#111] border border-[#262626] rounded px-2 py-1 text-[11px] text-white text-center"
                                        data-testid={`sidepot-inline-places-${sidepot.id}`}
                                      />
                                      <button
                                        type="button"
                                        disabled={sidepotCalcing}
                                        onClick={(e) => { e.stopPropagation(); handleInlineCalcSidepot(sidepot); }}
                                        className="ml-auto px-3 py-1 rounded bg-[#FACC15] text-black text-[11px] font-semibold hover:bg-[#FACC15]/80 disabled:opacity-50 disabled:cursor-not-allowed"
                                        data-testid={`sidepot-inline-calc-btn-${sidepot.id}`}
                                      >
                                        {sidepotCalcing ? 'Calculating…' : 'Calculate Payouts'}
                                      </button>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                            {isDiv && sidepotResults.division_payouts && Object.keys(sidepotResults.division_payouts).length > 0 ? (
                              <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                                {Object.entries(sidepotResults.division_payouts).sort(([a],[b])=>a.localeCompare(b)).map(([divName, divData]) => {
                                  // Backend `division_payouts[div].payouts` stores {place, amount, ...}
                                  // without rider info. Merge in rider/horse/time from `results`
                                  // (scoped to this division) so the standings show names + times.
                                  const divRiders = (sidepotResults.results || [])
                                    .filter(r => (r.division || r.div || r.division_label) === divName)
                                    .sort((a, b) => (a.division_place || a.place || 999) - (b.division_place || b.place || 999));
                                  return (
                                  <div key={divName}>
                                    <p className="text-[10px] font-bold text-[#0096FF] uppercase tracking-wider mb-1">{divName} &middot; {divRiders.length || (divData.entries || 0)} entries</p>
                                    {divRiders.length === 0 ? (
                                      <p className="text-[10px] text-[#555] italic px-2 pb-1">No riders placed in this division</p>
                                    ) : (
                                      divRiders.map((r, i) => {
                                        const place = r.division_place || r.place;
                                        const payout = (divData.payouts || []).find(p => p.place === place);
                                        return (
                                          <div key={`${divName}-${r.entry_id || i}`} className="flex items-center justify-between px-2 py-1 text-[11px]">
                                            <span className="text-white">
                                              <span className="font-bold text-[#FACC15] mr-1.5">{r.tied || payout?.tied ? `T${place}` : place}</span>
                                              {r.rider_name}{r.horse_name ? ` / ${r.horse_name}` : ''}
                                            </span>
                                            <span className="text-[#9E9E9E]">
                                              {r.final_time != null ? <span className="mr-2" style={{fontFamily:'JetBrains Mono'}}>{Number(r.final_time).toFixed(3)}</span> : null}
                                              {payout && <span className="text-[#34C759] font-semibold">${payout.amount}</span>}
                                            </span>
                                          </div>
                                        );
                                      })
                                    )}
                                  </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
                                {(sidepotResults.results || []).length === 0 ? (
                                  (sidepotResults.entrants || []).length === 0 ? (
                                    <p className="text-[11px] text-[#555] text-center py-3">No entrants yet — when riders roll over or enter this sidepot, they'll show up here.</p>
                                  ) : (
                                    <div data-testid={`sidepot-entrants-${sidepot.id}`}>
                                      <p className="text-[10px] font-bold text-[#0096FF] uppercase tracking-wider mb-1.5 px-1">
                                        Who&apos;s in &middot; {sidepotResults.entrants.length} entered &middot; awaiting scores
                                      </p>
                                      {(sidepotResults.entrants || []).map((e, i) => (
                                        <div
                                          key={e.entry_id || i}
                                          className="flex items-center justify-between px-2 py-1 text-[11px]"
                                          data-testid={`sidepot-entrant-${e.entry_id}`}
                                        >
                                          <span className="text-white">
                                            {e.rider_name}
                                            {e.horse_name ? <span className="text-[#9E9E9E]"> / {e.horse_name}</span> : null}
                                          </span>
                                          <span className="flex items-center gap-1.5">
                                            {e.is_rollover && (
                                              <span className="text-[9px] font-bold text-[#FACC15] bg-[#FACC15]/15 border border-[#FACC15]/30 rounded px-1.5 py-0.5 uppercase tracking-wider">Rollover</span>
                                            )}
                                            <span className="text-[9px] font-bold text-[#9E9E9E]">Pending</span>
                                            {isShowOwner && (
                                              <button
                                                type="button"
                                                onClick={() => setRemoveFromSidepotConfirm({
                                                  entry_id: e.entry_id,
                                                  rider_name: e.rider_name,
                                                  horse_name: e.horse_name,
                                                  is_rollover: e.is_rollover,
                                                  sidepot_id: sidepot.id,
                                                  sidepot_name: sidepot.name,
                                                  sidepot_fee: Number(sidepot.entry_fee || 0),
                                                })}
                                                className="text-[#FF3B30] hover:bg-[#FF3B30]/15 rounded p-0.5 transition-colors"
                                                title={`Remove ${e.rider_name} from this sidepot`}
                                                data-testid={`remove-from-sidepot-${e.entry_id}`}
                                              >
                                                <X size={11} weight="bold" />
                                              </button>
                                            )}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  )
                                ) : (
                                  (sidepotResults.results || []).map((r, i) => {
                                    // For divisioned sidepots, look up the
                                    // rider's DIVISION payout by their
                                    // (division, division_place) — what
                                    // they'll actually receive. Falls back
                                    // to the legacy single-pool payout
                                    // lookup for non-divisioned sidepots.
                                    const divPayouts = sidepotResults.division_payouts || {};
                                    const divInfo = r.division && divPayouts[r.division];
                                    const divPayout = divInfo
                                      ? (divInfo.payouts || []).find(p => p.place === r.division_place)
                                      : null;
                                    const overallPayout = (sidepotResults.payouts || []).find(p => p.place === r.place);
                                    const payout = divPayout || overallPayout;
                                    const displayPlace = r.division && r.division_place
                                      ? `${r.division}-${r.division_tied ? 'T' : ''}${r.division_place}`
                                      : (r.tied ? `T${r.place}` : r.place);
                                    const dc = getDivisionColors(r.division);
                                    return (
                                      <div key={r.entry_id || i} className="flex items-center justify-between px-2 py-1 text-[11px]" data-testid={`sidepot-result-${r.entry_id}`}>
                                        <span className="text-white flex items-center gap-1.5 min-w-0">
                                          <span className={`font-bold flex-shrink-0 ${r.division ? dc.placeText : 'text-[#FACC15]'}`}>{displayPlace}</span>
                                          <span className="truncate">{r.rider_name}{r.horse_name ? ` / ${r.horse_name}` : ''}</span>
                                          {r.division && !divInfo && (
                                            <span className={`text-[9px] font-bold ${dc.text} ${dc.bg} border ${dc.border} rounded px-1 py-0.5 uppercase flex-shrink-0`}>{r.division}</span>
                                          )}
                                        </span>
                                        <span className="flex items-center gap-2 text-[#9E9E9E] flex-shrink-0">
                                          {r.final_time != null ? <span style={{fontFamily:'JetBrains Mono'}}>{Number(r.final_time).toFixed(3)}</span> : null}
                                          {payout && <span className="text-[#34C759] font-semibold">${payout.amount}</span>}
                                          {isShowOwner && (
                                            <button
                                              type="button"
                                              onClick={() => setRemoveFromSidepotConfirm({
                                                entry_id: r.entry_id,
                                                rider_name: r.rider_name,
                                                horse_name: r.horse_name,
                                                is_rollover: false,
                                                sidepot_id: sidepot.id,
                                                sidepot_name: sidepot.name,
                                                sidepot_fee: Number(sidepot.entry_fee || 0),
                                              })}
                                              className="text-[#FF3B30] hover:bg-[#FF3B30]/15 rounded p-0.5 transition-colors"
                                              title={`Remove ${r.rider_name} from this sidepot`}
                                              data-testid={`remove-from-sidepot-${r.entry_id}`}
                                            >
                                              <X size={11} weight="bold" />
                                            </button>
                                          )}
                                        </span>
                                      </div>
                                    );
                                  })
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {isAdmin && <ReservationsTable reservations={reservations} />}
      {isAdmin && <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"><StaffReservations showId={id} /></div>}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <AveragesPanel show={show} isAdmin={isAdmin} />
        <TargetPanel show={show} isAdmin={isAdmin} />
        {isAdmin && <div className="mt-6"><ActivityFeed showId={id} /></div>}
      </div>

      <DivisionModal
        show={show} configuringClass={configuringClass} divisionType={divisionType} divisionSplit={divisionSplit}
        onSetDivisionType={setDivisionType} onSetDivisionSplit={setDivisionSplit}
        onSave={handleSaveDivisionConfig} onCancel={() => setConfiguringClass(null)}
        onSetRolloverFrom={handleSetRolloverFrom}
      />

      {sidepotModalOpen && (
        <SidepotEditModal
          showId={id}
          classes={show.classes || []}
          sidepot={sidepotBeingEdited}
          onClose={() => { setSidepotModalOpen(false); setSidepotBeingEdited(null); }}
          onSaved={() => { loadShow(); if (selectedSidepot) setSelectedSidepot(null); setSidepotResults(null); }}
        />
      )}
      {showSponsorModal && (
        <SponsorShowModal show={show} onClose={() => setShowSponsorModal(false)} />
      )}

      {showQrModal && (
        <ShowQRCodeModal
          show={show}
          url={publicShowUrl}
          onClose={() => setShowQrModal(false)}
          onGenerated={(info) => setShow((prev) => prev ? { ...prev, qr_generated_at: info.qr_generated_at, qr_generated_by: info.qr_generated_by } : prev)}
        />
      )}

      {/* Confirmation modal for removing a single rider from a sidepot.
          Producer/admin only — the trigger buttons (✗ on entrants/results
          lists) are gated by `isShowOwner`. Refund choice is yes/no and
          relayed to the backend so the toast/audit copy reflects intent. */}
      {removeFromSidepotConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          data-testid="remove-from-sidepot-modal"
        >
          <div className="bg-[#161616] border border-[#262626] rounded-xl p-5 w-full max-w-md">
            <div className="flex items-start gap-3 mb-3">
              <div className="p-2 bg-[#FF3B30]/15 border border-[#FF3B30]/30 rounded-lg">
                <Warning size={20} weight="fill" className="text-[#FF3B30]" />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-black text-white" style={{fontFamily: 'Outfit'}}>
                  Remove from {removeFromSidepotConfirm.sidepot_name}?
                </h3>
                <p className="text-xs text-[#9E9E9E] mt-0.5">
                  This pulls <span className="text-white font-semibold">{removeFromSidepotConfirm.rider_name}</span>
                  {removeFromSidepotConfirm.horse_name ? <> / <span className="text-[#9E9E9E]">{removeFromSidepotConfirm.horse_name}</span></> : null}
                  {' '}out of this sidepot. Their class entry is untouched.
                </p>
              </div>
            </div>

            {removeFromSidepotConfirm.is_rollover && (
              <div className="mb-3 px-3 py-2 bg-[#FACC15]/10 border border-[#FACC15]/30 rounded-md flex items-start gap-2" data-testid="rollover-warning">
                <Warning size={14} weight="fill" className="text-[#FACC15] mt-0.5 flex-shrink-0" />
                <p className="text-[11px] text-[#FACC15]">
                  This is a <strong>rollover</strong> enrollment. Removing it only undoes the sidepot link — the rider's original class entry stays intact.
                </p>
              </div>
            )}

            <div className="bg-[#0A0A0A] border border-[#262626] rounded-md p-3 mb-4">
              <p className="text-[10px] uppercase tracking-wider text-[#9E9E9E] font-bold mb-1">Sidepot fee</p>
              <p className="text-lg font-black text-white" style={{fontFamily: 'JetBrains Mono'}}>
                ${(removeFromSidepotConfirm.sidepot_fee || 0).toFixed(2)}
              </p>
              <p className="text-[10px] text-[#666] mt-1">
                Saved sidepot payouts are auto-reset on removal so the next "Calculate" reflects the new pool.
              </p>
            </div>

            <div className="space-y-2">
              <button
                type="button"
                disabled={!!removeFromSidepotConfirm.removing}
                onClick={() => handleRemoveFromSidepot({ refund: true })}
                className="w-full px-4 py-2.5 bg-[#FF9500] hover:bg-[#FF9500]/90 text-white font-bold rounded-lg text-sm disabled:opacity-50"
                data-testid="remove-with-refund-btn"
              >
                Remove &amp; Refund ${(removeFromSidepotConfirm.sidepot_fee || 0).toFixed(2)}
              </button>
              <button
                type="button"
                disabled={!!removeFromSidepotConfirm.removing}
                onClick={() => handleRemoveFromSidepot({ refund: false })}
                className="w-full px-4 py-2.5 bg-[#FF3B30]/15 hover:bg-[#FF3B30]/25 border border-[#FF3B30]/40 text-[#FF3B30] font-bold rounded-lg text-sm disabled:opacity-50"
                data-testid="remove-no-refund-btn"
              >
                Remove (no refund — forfeit fee)
              </button>
              <button
                type="button"
                disabled={!!removeFromSidepotConfirm.removing}
                onClick={() => setRemoveFromSidepotConfirm(null)}
                className="w-full px-4 py-2 text-[#9E9E9E] hover:text-white text-sm disabled:opacity-50"
                data-testid="remove-cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShowDetail;
